const pow = Math.pow;
const sqrt = Math.sqrt;
const sin = Math.sin;
const cos = Math.cos;
const tan = Math.tan;
const PI = Math.PI;
const c1 = 1.70158;
const c2 = c1 * 1.525;
const c3 = c1 + 1;
const c4 = (2 * PI) / 3;
const c5 = (2 * PI) / 4.5;

const bounceOut = (x: number): number => {
    const n1 = 7.5625;
    const d1 = 2.75;

    if (x < 1 / d1) {
        return n1 * x * x;
    } else if (x < 2 / d1) {
        return n1 * (x -= 1.5 / d1) * x + 0.75;
    } else if (x < 2.5 / d1) {
        return n1 * (x -= 2.25 / d1) * x + 0.9375;
    } else {
        return n1 * (x -= 2.625 / d1) * x + 0.984375;
    }
};

function buildPowEasings(factor: number): { in: Ease; out: Ease; inOut: Ease } {
    if (factor <= 1) {
        throw new Error(
            'Factor must be greater than 1 to produce a non-linear easing; use `Easings.linear` for a linear easing function'
        );
    }

    return {
        in: (t: number): number => pow(t, factor),
        out: (t: number): number => 1 - pow(1 - t, factor),
        inOut: (t: number): number => (t < 0.5 ? pow(2, factor - 1) * pow(t, factor) : 1 - pow(-2 * t + 2, factor) / 2),
    };
}

export const Easings = {
    // Combine easings or add your own modification.
    modify:
        (ease: Ease, modifier: Ease): Ease =>
        (t: number): number =>
            modifier(ease(t)),

    // no easing, no acceleration
    linear: (t: number): number => t,

    // Quadratic
    quad: buildPowEasings(2),

    // Cubic
    cubic: buildPowEasings(3),

    // Quartic
    quart: buildPowEasings(4),

    // Quintic
    quint: buildPowEasings(5),

    // Power
    pow: buildPowEasings,

    // https://easings.net/

    // Sine
    sine: {
        in: (t: number): number => 1 - cos((t * PI) / 2),
        out: (t: number): number => sin((t * PI) / 2),
        inOut: (t: number): number => -(cos(PI * t) - 1) / 2,
    },

    // Exponential
    expo: {
        in: (t: number): number => (t === 0 ? 0 : pow(2, 10 * t - 10)),
        out: (t: number): number => (t === 1 ? 1 : 1 - pow(2, -10 * t)),
        inOut: (t: number): number =>
            t === 0 ? 0 : t === 1 ? 1 : t < 0.5 ? pow(2, 20 * t - 10) / 2 : (2 - pow(2, -20 * t + 10)) / 2,
    },

    // Circular
    circ: {
        in: (t: number): number => 1 - sqrt(1 - pow(t, 2)),
        out: (t: number): number => sqrt(1 - pow(t - 1, 2)),
        inOut: (t: number): number =>
            t < 0.5 ? (1 - sqrt(1 - pow(2 * t, 2))) / 2 : (sqrt(1 - pow(-2 * t + 2, 2)) + 1) / 2,
    },

    // Overshoot and back
    back: {
        in: (t: number): number => c3 * t * t * t - c1 * t * t,
        out: (t: number): number => 1 + c3 * pow(t - 1, 3) + c1 * pow(t - 1, 2),
        inOut: (t: number): number =>
            t < 0.5
                ? (pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
                : (pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2,
    },

    // Elastic
    elastic: {
        in: (t: number): number => (t === 0 ? 0 : t === 1 ? 1 : -pow(2, 10 * t - 10) * sin((t * 10 - 10.75) * c4)),
        out: (t: number): number => (t === 0 ? 0 : t === 1 ? 1 : pow(2, -10 * t) * sin((t * 10 - 0.75) * c4) + 1),
        inOut: (t: number): number =>
            t === 0
                ? 0
                : t === 1
                  ? 1
                  : t < 0.5
                    ? -(pow(2, 20 * t - 10) * sin((20 * t - 11.125) * c5)) / 2
                    : (pow(2, -20 * t + 10) * sin((20 * t - 11.125) * c5)) / 2 + 1,
    },

    // Bounce
    bounce: {
        in: (t: number): number => 1 - bounceOut(1 - t),
        out: bounceOut,
        inOut: (t: number): number => (t < 0.5 ? (1 - bounceOut(1 - 2 * t)) / 2 : (1 + bounceOut(2 * t - 1)) / 2),
    },

    sin: {
        in: (t: number): number => pow(sin(t * PI), 2),
        out: (t: number): number => -pow(sin(t * PI), 2),
        inOut: (t: number): number => sin(t * PI),
    },

    cos: {
        in: (t: number): number => pow(cos(t * PI), 2),
        out: (t: number): number => -pow(cos(t * PI), 2),
        inOut: (t: number): number => cos(t * PI),
    },

    tan: {
        in: (t: number): number => tan(t) / tan(1),
        out: (t: number): number => -tan(t) / tan(1) + 1,
        inOut: (t: number): number => tan(t),
    },
};

export type Ease = (t: number) => number;
